import { useI18n } from "@/context/I18nContext";

import { useEffect, useState } from "react";
import { loadPdfjs } from "@/lib/pdfLazy";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Download, X, Printer } from "lucide-react";


// Preview of a generated poster PDF ({ data: ArrayBuffer, save: fn }) with download/close.
export const PosterPreview = ({ pdf, onClose, title = "Contrôle de l'affiche", description = "Vérifiez l'affiche avant de la télécharger ou de l'imprimer.", testid = "poster-preview", printable = false }) => {
  const { t } = useI18n();
  const [pages, setPages] = useState([]);
  const print = () => {
    const url = URL.createObjectURL(new Blob([pdf.data.slice(0)], { type: "application/pdf" }));
    const f = document.createElement("iframe");
    f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
    f.src = url;
    f.onload = () => { f.contentWindow.focus(); f.contentWindow.print(); };
    document.body.appendChild(f);
    setTimeout(() => { f.remove(); URL.revokeObjectURL(url); }, 120000);
  };

  useEffect(() => {
    if (!pdf) return;
    let cancelled = false;
    setPages([]);
    (async () => {
      const pdfjsLib = await loadPdfjs();
      const doc = await pdfjsLib.getDocument({ data: pdf.data.slice(0) }).promise;
      const out = [];
      for (let n = 1; n <= doc.numPages; n++) {
        const page = await doc.getPage(n);
        const vp = page.getViewport({ scale: 2 });
        const c = document.createElement("canvas");
        c.width = vp.width; c.height = vp.height;
        await page.render({ canvasContext: c.getContext("2d"), viewport: vp }).promise;
        out.push(c.toDataURL("image/jpeg", 0.9));
      }
      if (!cancelled) setPages(out);
    })();
    return () => { cancelled = true; };
  }, [pdf]);

  return (
    <Dialog open={!!pdf} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[96vw] max-w-3xl h-[94vh] p-0 overflow-hidden flex flex-col gap-0" data-testid={testid}>
        <div className="px-5 pt-4 pb-3 border-b border-border">
          <DialogTitle>{t(title)}</DialogTitle>
          <DialogDescription className="mt-1">{t(description)}</DialogDescription>
        </div>
        <div className="flex-1 bg-muted overflow-y-auto p-3 sm:p-5 space-y-4" data-testid={`${testid}-page`}>
          {pages.length
            ? pages.map((src, i) => <img key={i} src={src} alt={`Page ${i + 1}`} className="w-full max-w-[640px] mx-auto block bg-white shadow-md" data-testid={i === 0 ? `${testid}-image` : `${testid}-image-${i + 1}`} />)
            : <div className="flex items-center justify-center h-full text-muted-foreground gap-2"><Loader2 className="w-5 h-5 animate-spin" /> {t("Préparation du PDF…")}</div>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
          <Button variant="outline" onClick={onClose} className="gap-2" data-testid={`${testid}-close`}><X className="w-4 h-4" /> {t("Fermer")}</Button>
          {printable && <Button variant="outline" onClick={print} disabled={!pages.length} className="gap-2" data-testid={`${testid}-print`}><Printer className="w-4 h-4" /> {t("Imprimer")}</Button>}
          <Button onClick={() => pdf?.save()} className="gap-2" data-testid={`${testid}-download`}><Download className="w-4 h-4" /> {t("Télécharger")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
